import { RootState } from '@store';
import {
  TaskItem,
  AutocompleteFieldItem,
} from '@domains/documents-review/interfaces/interfaces';
import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { CountryCode } from '@domains/country';
import { normalizeAutocompleteValue } from '@domains/documents-review/utils/dateNormalization';

export enum ReviewOption {
  APPROVE = 'approve',
  REJECT = 'reject',
  ABORT = 'aborted',
}

export enum TabsDocumentsReviewOption {
  DOCUMENTS = 'documents',
  HISTORY = 'history',
  AUTOCOMPLETE = 'autocomplete',
  POLICIES_HISTORY = 'policies-history',
}

export type DocumentType = {
  id: string;
  title: string;
  fileUrl: string;
};

export interface DocumentReviewState {
  selectedOption: ReviewOption | null;
  selectedTask: TaskItem | null;
  showModal: ReviewOption | null;
  selectedDocument: string | null;
  documents: DocumentType[];
  nidSelected: string | null;
  pricingTasks: TaskItem[];
  tabsDocumentsReview: TabsDocumentsReviewOption;
  autocompleteFieldsByCountry: Record<CountryCode, AutocompleteFieldItem[]>;
  selectedTasks: string[];
}

export const initialState: DocumentReviewState = {
  selectedOption: null,
  selectedTask: null,
  showModal: null,
  selectedDocument: null,
  documents: [],
  nidSelected: null,
  pricingTasks: [],
  tabsDocumentsReview: TabsDocumentsReviewOption.DOCUMENTS,
  autocompleteFieldsByCountry: {} as Record<
    CountryCode,
    AutocompleteFieldItem[]
  >,
  selectedTasks: [],
};

export const domainDocumentReviewSlice = createSlice({
  name: 'domainDocumentReview',
  initialState,
  reducers: {
    setSelectedOption: (state, action) => {
      state.selectedOption = action.payload;
    },
    setSelectedTask: (state, action) => {
      state.selectedTask = action.payload;
    },
    setShowModal: (state, action) => {
      state.showModal = action.payload;
    },
    setSelectedDocument: (state, action) => {
      state.selectedDocument = action.payload;
    },
    setDocuments(state, action: PayloadAction<DocumentType[]>) {
      state.documents = action.payload;
    },
    setNidSelected: (state, action) => {
      state.nidSelected = action.payload;
    },
    setPricingTasks: (state, action) => {
      state.pricingTasks = action.payload;
    },
    setTabsDocumentsReview: (state, action) => {
      state.tabsDocumentsReview = action.payload;
    },
    setSelectedTasks: (state, action) => {
      const id = action.payload;
      if (state.selectedTasks.includes(id)) {
        state.selectedTasks = state.selectedTasks.filter(
          taskId => taskId !== id
        );
      } else {
        state.selectedTasks.push(id);
      }
    },
    clearSelectedTasks: state => {
      state.selectedTasks = [];
    },
    setDefaultState: () => {
      return initialState;
    },
    setAutocompleteFieldsForCountry: (
      state,
      action: PayloadAction<{
        country: CountryCode;
        items: AutocompleteFieldItem[];
      }>
    ) => {
      const { country, items } = action.payload;
      state.autocompleteFieldsByCountry = {
        ...state.autocompleteFieldsByCountry,
        [country]: items,
      } as Record<CountryCode, AutocompleteFieldItem[]>;
    },
    applyAutocompleteSuggestion: (
      state,
      action: PayloadAction<{
        country: CountryCode;
        field: string;
        suggestedValue: string;
        model: string;
        userFeedback?: number;
        suggestionId?: number | null;
        initialConfidence?: number;
        agentComment?: string;
      }>
    ) => {
      const {
        country,
        field,
        suggestedValue,
        model,
        userFeedback = 100,
        suggestionId = null,
        initialConfidence,
        agentComment,
      } = action.payload;
      const items = state.autocompleteFieldsByCountry?.[country];
      if (!items) {
        state.autocompleteFieldsByCountry[country] =
          [] as AutocompleteFieldItem[];
      }
      const list = state.autocompleteFieldsByCountry[
        country
      ] as AutocompleteFieldItem[];
      let target = list.find(i => i.field === field);
      if (!target) {
        target = {
          field,
          suggestions: [],
          finalValue: '',
          userFeedback: 0,
          selectedModel: '',
          selectedSuggestionId: null,
          finalConfidence: 0,
          agentComment: undefined,
          reviewed: false,
        } as AutocompleteFieldItem;
        list.push(target);
      }
      const normalizedValue = normalizeAutocompleteValue(
        field,
        suggestedValue
      ) as string;

      target.finalValue = normalizedValue;
      target.selectedModel = model;
      target.userFeedback = userFeedback;
      target.selectedSuggestionId = suggestionId ?? null;
      target.finalConfidence =
        typeof initialConfidence === 'number'
          ? initialConfidence
          : target.finalConfidence;
      target.agentComment = agentComment;
      target.reviewed = true;

      if (
        field === 'adminPrice' &&
        suggestedValue &&
        suggestedValue.trim() !== ''
      ) {
        let hasAdminTarget = list.find(i => i.field === 'hasAdmin');
        if (!hasAdminTarget) {
          hasAdminTarget = {
            field: 'hasAdmin',
            suggestions: [],
            finalValue: 0,
            userFeedback: 0,
            selectedModel: '',
            selectedSuggestionId: null,
            finalConfidence: 0,
            agentComment: undefined,
            reviewed: false,
          } as AutocompleteFieldItem;
          list.push(hasAdminTarget);
        }

        // Solo setear hasAdmin si no tiene un valor ya establecido
        if (
          !hasAdminTarget.finalValue ||
          hasAdminTarget.finalValue === 0 ||
          hasAdminTarget.finalValue === ''
        ) {
          hasAdminTarget.finalValue = 1;
          hasAdminTarget.selectedModel = 'auto-set';
          hasAdminTarget.userFeedback = 100;
          hasAdminTarget.reviewed = true;
          hasAdminTarget.agentComment =
            'Auto-completado cuando se aplicó sugerencia para adminPrice';
        }
      }
    },
    resetAutocompleteFieldOnManualChange: (
      state,
      action: PayloadAction<{ country: CountryCode; field: string }>
    ) => {
      const { country, field } = action.payload;
      const items = state.autocompleteFieldsByCountry?.[country];
      if (!items) {
        state.autocompleteFieldsByCountry[country] =
          [] as AutocompleteFieldItem[];
      }
      const list = state.autocompleteFieldsByCountry[
        country
      ] as AutocompleteFieldItem[];
      let target = list.find(i => i.field === field);
      if (!target) {
        target = {
          field,
          suggestions: [],
          finalValue: '',
          userFeedback: 0,
          selectedModel: '',
          reviewed: false,
        } as AutocompleteFieldItem;
        list.push(target);
      }
      target.finalValue = '';
      target.selectedModel = '';
      target.userFeedback = 0;
      target.reviewed = false;
    },
    resetAutocompleteFields: (
      state,
      action: PayloadAction<
        | {
            feedbackByCountry?: Record<CountryCode, AutocompleteFieldItem[]>;
          }
        | undefined
      >
    ) => {
      // Inicializa estructura vacía; opcionalmente hidrata con payload (tests)
      const payload = action?.payload;
      state.autocompleteFieldsByCountry = (payload?.feedbackByCountry ||
        ({} as Record<CountryCode, AutocompleteFieldItem[]>)) as Record<
        CountryCode,
        AutocompleteFieldItem[]
      >;
    },
  },
});

export const {
  setSelectedOption,
  setSelectedTask,
  setShowModal,
  setSelectedDocument,
  setDocuments,
  setNidSelected,
  setDefaultState,
  setTabsDocumentsReview,
  setPricingTasks,
  setAutocompleteFieldsForCountry,
  applyAutocompleteSuggestion,
  resetAutocompleteFieldOnManualChange,
  resetAutocompleteFields,
  setSelectedTasks,
  clearSelectedTasks,
} = domainDocumentReviewSlice.actions;

export default domainDocumentReviewSlice.reducer;

// selectors.
export const getSelectedOption = (state: RootState) =>
  state.domainDocumentReview.selectedOption;

export const getRejectForm = (state: RootState) =>
  state.domainDocumentReview.selectedOption === ReviewOption.REJECT;

export const getSelectedTask = (state: RootState) =>
  state.domainDocumentReview.selectedTask;

export const getShowModal = (state: RootState) =>
  state.domainDocumentReview.showModal;

export const getSelectedDocument = (state: RootState) =>
  state.domainDocumentReview.selectedDocument;

export const getDocuments = (state: RootState) =>
  state.domainDocumentReview.documents;

export const getNidSelected = (state: RootState) =>
  state.domainDocumentReview.nidSelected;

export const getPricingTasks = (state: RootState) =>
  state.domainDocumentReview.pricingTasks;

export const getTabsDocumentsReview = (state: RootState) =>
  state.domainDocumentReview.tabsDocumentsReview;

export const getAutocompleteFieldsByCountry = (state: RootState) =>
  state.domainDocumentReview.autocompleteFieldsByCountry;

export const getSelectedTasks = (state: RootState) =>
  state.domainDocumentReview.selectedTasks;
